import React from 'react';
import { Sparkles, ArrowDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { trackCTAClick } from '../utils/analytics';

/**
 * Hero v2 — premium, conversion-first.
 *
 * Removed vs v1:
 *   • Purple/cyan gradient in the H1 (read as "AI slop", hurt trust).
 *   • Decorative blurred blob behind the title.
 *   • "POWERED BY AI" pill badge above the H1.
 *   • 4-tile proof bar with gradient icon boxes.
 *   • Competing secondary "Find Best Deals" CTA.
 *   • Inline city-search widget (users can access via nav; keeps hero focused).
 *
 * Added / kept:
 *   • ONE primary CTA — "Plan my trip with AI" — anchors to #ai-chat.
 *   • Sub-anchor "or browse today's deals ↓" (link-style, non-competing).
 *   • Premium trust strip — plain-text stats separated by dots.
 *   • Partner wordmarks strip immediately below (grayscale, non-clickable).
 *
 * Rationale:
 *   Cast-iron single CTA + trust hierarchy at Booking.com / Google Travel
 *   density. Trust comes from real numbers + real partner names, not from
 *   badges or gradients. Every pixel of copy is user-editable via `t(...)`.
 */
export default function Hero() {
  const { t } = useLanguage();

  // Show actual product capabilities instead of unverified traffic,
  // supplier-inventory or accreditation counts.
  const stats = [
    { value: 'AI', key: 'hero.stat.destinations', fallback: 'Trip planner' },
    { value: 'BG/EN', key: 'hero.stat.airlines', fallback: 'Languages' },
    { value: 'Multi-city', key: 'hero.stat.partners', fallback: 'Trip routes' },
    { value: '0€', key: 'hero.stat.fees', fallback: 'Planning fee' },
  ];

  const partners = [
    { name: 'Booking.com', scope: 'HOTELS' },
    { name: 'Aviasales', scope: 'FLIGHTS' },
  ];

  return (
    <div className="relative w-full overflow-hidden">
      {/* Ambient background — extremely soft radial glow behind the title.
          Deliberately low-opacity so it adds depth without ever competing
          with the H1. Editorial magazines use similar treatments to lift a
          hero out of the surrounding white space. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(60%_60%_at_50%_0%,rgba(37,99,235,0.10),transparent_65%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-24 h-[260px] bg-[radial-gradient(50%_50%_at_50%_50%,rgba(15,23,42,0.05),transparent_70%)]"
      />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-4">
        {/* Editorial eyebrow — real, verifiable capabilities only.
            Small, quiet, uppercase tracking-widest — pure "magazine" tone. */}
        <div className="flex items-center justify-center gap-2 mb-5" data-testid="hero-eyebrow">
          <span className="w-6 h-px bg-gradient-to-r from-transparent to-gold-400/60" aria-hidden />
          <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-semibold text-gold-700">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-gold-500 animate-pulse" aria-hidden />
            {t('hero.eyebrow') || 'AI-Powered · Live Prices · IATA Partners'}
          </span>
          <span className="w-6 h-px bg-gradient-to-l from-transparent to-gold-400/60" aria-hidden />
        </div>

        {/* Title — signature Cormorant Garamond serif per V4 rebrand.
            The italic accent line is elevated in Midnight Blue with a
            gold hairline underline glow instead of the previous cyan-blue. */}
        <h1
          className="font-serif font-normal text-[3rem] sm:text-6xl lg:text-7xl text-midnight-900 leading-[1.02] tracking-tight text-center max-w-3xl mx-auto"
          data-testid="hero-title"
        >
          {t('hero.titlePrefix') || 'Your personal'}{' '}
          <span className="relative inline-block italic text-midnight-800">
            {t('hero.titleAccent') || 'AI travel'}
            <span
              aria-hidden
              className="pointer-events-none absolute -inset-x-1 -bottom-1 h-8 bg-[radial-gradient(50%_100%_at_50%_0%,rgba(201,162,74,0.28),transparent_75%)]"
            />
          </span>{' '}
          {t('hero.titleSuffix') || 'assistant'}
        </h1>

        {/* Editorial signature mark — soft-gold diamond glyph + "Sofia → World" identity. */}
        <div className="mt-4 flex items-center justify-center gap-3" aria-hidden data-testid="hero-signature-mark">
          <span className="w-8 h-px bg-gold-300/60" />
          <span className="text-gold-500 text-sm">◆</span>
          <span className="text-[10px] uppercase tracking-[0.32em] font-semibold text-gold-700">
            {t('hero.identityMark') || 'Sofia → World'}
          </span>
          <span className="text-gold-500 text-sm">◆</span>
          <span className="w-8 h-px bg-gold-300/60" />
        </div>

        <p
          className="mt-4 text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed text-center"
          data-testid="hero-subtitle"
        >
          {t('hero.description')}
        </p>

        {/* Single primary CTA + a text sub-anchor to the deals section.
            No competing button — keeps the funnel obvious. */}
        <div className="mt-7 flex flex-col items-center gap-2.5">
          <a
            href="#ai-chat"
            data-testid="hero-plan-trip-cta"
            onClick={() => trackCTAClick({ cta_id: 'hero-plan-trip', screen: 'home', variant: 'primary', destination: 'ai_chat' })}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-600/40 transition-all transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 min-h-[52px] text-base"
          >
            <Sparkles className="w-4 h-4" aria-hidden />
            <span>{t('hero.planTripCta')}</span>
          </a>

          <a
            href="#deals"
            data-testid="hero-browse-deals-link"
            onClick={() => trackCTAClick({ cta_id: 'hero-browse-deals', screen: 'home', variant: 'text', destination: 'deals' })}
            className="inline-flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-blue-700 transition-colors"
          >
            {t('hero.orBrowseDeals') || 'or browse today\'s deals'}
            <ArrowDown className="w-3.5 h-3.5" aria-hidden />
          </a>
        </div>

        {/* Premium trust strip — real numbers, plain text.
            Deliberately un-icon'd. Google Travel / Skyscanner pattern. */}
        <div
          data-testid="hero-trust-stats"
          className="mt-10 pt-6 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-4 max-w-3xl mx-auto"
        >
          {stats.map(({ value, key, fallback }) => {
            const label = t(key);
            const display = label && label !== key ? label : fallback;
            return (
              <div key={key} className="text-center" data-testid={`hero-stat-${key.split('.').pop()}`}>
                <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 tabular-nums leading-none">
                  {value}
                </div>
                <div className="mt-1.5 text-[11px] uppercase tracking-widest font-semibold text-gray-500">
                  {display}
                </div>
              </div>
            );
          })}
        </div>

        {/* Partner wordmarks — inline right below the stats. Monochrome
            soft-gold on ivory. Booking.com and Aviasales are search destinations, not booking guarantees. */}
        <div className="mt-7 pt-6 border-t border-gold-200/60">
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-gold-700/70 text-center mb-4">
            {t('hero.trustedPartners') || 'In discreet partnership with'}
          </div>
          <div
            data-testid="hero-partner-strip"
            className="flex flex-wrap items-center justify-center gap-x-10 sm:gap-x-14 gap-y-3"
          >
            {partners.map((p) => (
              <div
                key={p.name}
                data-testid={`hero-partner-${p.name.toLowerCase().replace(/\W/g, '-')}`}
                className="inline-flex items-baseline gap-2 opacity-70 hover:opacity-100 transition-opacity"
              >
                <span className="font-serif text-lg sm:text-xl text-midnight-800 tracking-tight italic">{p.name}</span>
                <span className="text-[9px] uppercase tracking-[0.28em] font-semibold text-gold-600">
                  {p.scope}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
